/**
 * useActivityLog
 *
 * Tier 1 platform event logger — auto-fired throughout the app.
 * Does NOT mutate trait scores. For trait-mutating events use useTraitMutation instead.
 *
 * Usage:
 *   const { logEvent } = useActivityLog()
 *   logEvent('lesson_started', { topicId: 'math-101', pathId: 'broad_stem_tech' })
 */

import { useCallback } from 'react'
import { StorageService } from '@/hooks/services/storageService'
import { AuthStore } from '@/hooks/stores/AuthStore'
import {
  ActivityLogEntry,
  PlatformEventType,
  makeActivityId,
  currentTimeOfDay,
  deriveSentiment,
} from '@/types/activityLog'

// ─── Session ID ───────────────────────────────────────────────────────────────
// One stable ID per browser session, shared across all events in that session.

function getOrCreateSessionId(): string {
  const key = 'earlyscape_session_id'
  let id = sessionStorage.getItem(key)
  if (!id) {
    id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    sessionStorage.setItem(key, id)
  }
  return id
}

// ─── Append helper ────────────────────────────────────────────────────────────

function appendToStudentLog(userId: string, entry: ActivityLogEntry): void {
  const students: any[] = StorageService.read('students')
  const idx = students.findIndex(
    (s) => s.userId === userId || s._id === userId || s.email === AuthStore.getState().currentUser?.email
  )
  if (idx === -1) return
  const student = students[idx]
  // Keep platform events in a separate lightweight array so they don't bloat the heavy activityLog
  const platformLog: ActivityLogEntry[] = student.platformLog ?? []
  students[idx] = { ...student, platformLog: [entry, ...platformLog].slice(0, 500) }
  StorageService.write('students', students)
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useActivityLog() {
  const currentUser = AuthStore.getState().currentUser

  const logEvent = useCallback(
    (
      eventType: PlatformEventType,
      meta: Record<string, unknown> = {},
      extras: {
        subjectArea?: string
        topicFamilyId?: string
        careerPathId?: string
        durationSeconds?: number
        outcomeScore?: number
        outcomeRating?: 1 | 2 | 3 | 4 | 5
        tags?: string[]
      } = {}
    ): void => {
      if (!currentUser) return

      const entry: ActivityLogEntry = {
        id: makeActivityId(),
        userId: currentUser._id ?? '',
        eventCategory: 'platform',
        eventType,
        timestamp: new Date().toISOString(),
        sessionId: getOrCreateSessionId(),
        subjectArea: extras.subjectArea,
        topicFamilyId: extras.topicFamilyId,
        careerPathId: extras.careerPathId,
        durationSeconds: extras.durationSeconds,
        tags: extras.tags ?? [],
        meta,
        outcomeScore: extras.outcomeScore,
        outcomeRating: extras.outcomeRating,
        sentimentLabel: deriveSentiment(extras.outcomeScore, extras.outcomeRating),
      }

      appendToStudentLog(currentUser._id ?? '', entry)
    },
    [currentUser]
  )

  return { logEvent }
}

// ─── Session start helper ─────────────────────────────────────────────────────
// Call once on app mount (guarded by sessionStorage flag).

export function logSessionStart(): void {
  const flag = 'sessionLogged'
  if (sessionStorage.getItem(flag)) return
  sessionStorage.setItem(flag, '1')

  const user = AuthStore.getState().currentUser
  if (!user) return

  const entry: ActivityLogEntry = {
    id: makeActivityId(),
    userId: user._id ?? '',
    eventCategory: 'platform',
    eventType: 'session_start',
    timestamp: new Date().toISOString(),
    sessionId: getOrCreateSessionId(),
    tags: [],
    meta: { timeOfDay: currentTimeOfDay() },
  }

  const students: any[] = StorageService.read('students')
  const idx = students.findIndex(
    (s) => s.userId === user._id || s._id === user._id || s.email === user.email
  )
  if (idx === -1) return
  const student = students[idx]
  const platformLog: ActivityLogEntry[] = student.platformLog ?? []
  students[idx] = { ...student, platformLog: [entry, ...platformLog].slice(0, 500) }
  StorageService.write('students', students)
}
